import { noteIndex, usesFlats, getNoteName, DEGREE_LABELS } from './theory';

// Tuning: string 0 = 1st string (D), string 1 = 2nd (B), string 2 = 3rd (G),
//         string 3 = 4th (low D), string 4 = 5th (high G — short string, starts at fret 5)
// String 4 (5th string) uses the imaginary nut pitch D=2 so that fret 5 sounds G:
// (2 + 5) % 12 = 7 = G
export const BANJO_TUNING = [2, 11, 7, 2, 2];
export const TOTAL_FRETS_BANJO = 22;

// Banjo inlays feature the 10th fret instead of 9th
export const INLAY_FRETS = [3, 5, 7, 10, 12, 15, 17, 19, 22];
export const DOUBLE_INLAY_FRETS = [12, 22];

export const SCALE_FORMULAS = {
  'Major Scale':      [0, 2, 4, 5, 7, 9, 11],
  'Minor Scale':      [0, 2, 3, 5, 7, 8, 10],
  'Major Pentatonic': [0, 2, 4, 7, 9],
  'Minor Pentatonic': [0, 3, 5, 7, 10],
  'Major Arpeggio':   [0, 4, 7],
  'Minor Arpeggio':   [0, 3, 7],
  // Diatonic modes
  'Ionian':           [0, 2, 4, 5, 7, 9, 11],
  'Dorian':           [0, 2, 3, 5, 7, 9, 10],
  'Phrygian':         [0, 1, 3, 5, 7, 8, 10],
  'Lydian':           [0, 2, 4, 6, 7, 9, 11],
  'Mixolydian':       [0, 2, 4, 5, 7, 9, 10],
  'Aeolian':          [0, 2, 3, 5, 7, 8, 10],
  'Locrian':          [0, 1, 3, 5, 6, 8, 10],
};

// Offsets relative to the fret of the root string
// Arrays map to strings [1st (0), 2nd (1), 3rd (2), 4th (3)]
// majorOffsets produce a major triad; minorOffsets lower the 3rd by one fret
const CHORD_SHAPES = {
  'F Shape':    { rootString: 3, majorOffsets: [0, -2, -1, 0], minorOffsets: [0, -2, -2, 0] },  // Root on 4th string
  'D Shape':    { rootString: 1, majorOffsets: [1,  0, -1, 1], minorOffsets: [0,  0, -1, 0] },  // Root on 2nd string
  'Barre Shape': { rootString: 2, majorOffsets: [0,  0,  0, 0], minorOffsets: [0, -1,  0, 0] }, // Root on 3rd string
};

export function buildScaleNodes(rootName, formulaName, stringFocus, tonality = 'Major') {
  const rootIdx = noteIndex(rootName);
  const formula = SCALE_FORMULAS[formulaName] || SCALE_FORMULAS['Major Scale'];
  const flats   = usesFlats(rootName, formula, tonality.toLowerCase());
  const nodes = [];

  BANJO_TUNING.forEach((nutPitch, stringIdx) => {
    if (!stringFocus[stringIdx]) return;

    for (let fret = 0; fret <= TOTAL_FRETS_BANJO; fret++) {
      // 5th string is a short string — physically starts at fret 5
      if (stringIdx === 4 && fret < 5) continue;

      const noteIdx = (nutPitch + fret) % 12;
      const interval = (noteIdx - rootIdx + 12) % 12;

      if (formula.includes(interval)) {
        nodes.push({
          string: stringIdx,
          fret,
          isRoot: interval === 0,
          interval,
          noteName: getNoteName(noteIdx, flats),
        });
      }
    }
  });

  return nodes;
}

export function buildChordNodes(rootName, shapeName, tonality, stringFocus) {
  const rootIdx = noteIndex(rootName);
  const shape = CHORD_SHAPES[shapeName];
  if (!shape) return [];

  const isMajor = (tonality || 'Major') === 'Major';
  const offsets = isMajor ? shape.majorOffsets : shape.minorOffsets;
  const flats   = usesFlats(rootName, null, isMajor ? 'major' : 'minor');

  const nodes = [];
  const rootStringPitch = BANJO_TUNING[shape.rootString];

  // Find every occurrence of this shape up the neck
  for (let rFret = 0; rFret <= TOTAL_FRETS_BANJO; rFret++) {
    if ((rootStringPitch + rFret) % 12 !== rootIdx) continue;

    let isValid = true;
    const tempNodes = [];

    for (let s = 0; s < 4; s++) { // Chords use strings 0-3 only
      if (!stringFocus[s]) continue;
      const fret = rFret + offsets[s];

      if (fret < 0 || fret > TOTAL_FRETS_BANJO) {
        isValid = false;
        break;
      }

      const noteIdx = (BANJO_TUNING[s] + fret) % 12;
      const interval = (noteIdx - rootIdx + 12) % 12;

      tempNodes.push({
        string: s,
        fret,
        isRoot: interval === 0,
        interval,
        noteName: getNoteName(noteIdx, flats),
      });
    }

    if (isValid) nodes.push(...tempNodes);
  }

  return nodes;
}

// ── Page title + "Notes in the chord" row (banjo Chords, also used by dobro Chords) ──

/**
 * Page title for a major or minor triad chord: "G Major Chord", "G Minor Chord".
 * The banjo and dobro chord builders only know these two qualities.
 *
 * @param {string} key       Root note name.
 * @param {string} tonality  'Major' | 'Minor'
 * @returns {string}
 */
export function getChordTitle(key, tonality) {
  return `${key} ${tonality === 'Minor' ? 'Minor' : 'Major'} Chord`;
}

/**
 * The triad's notes for the "Notes in the chord" row, spelled exactly as the banjo and
 * dobro chord builders spell them on the fretboard (same usesFlats / getNoteName rule),
 * so the row always matches the dots.
 *
 * @param {string} key       Root note name.
 * @param {string} tonality  'Major' | 'Minor'
 * @returns {Array<{name: string, degree: string, isRoot: boolean}>} Empty for an unknown key.
 */
export function getChordNotes(key, tonality) {
  const rootIdx = noteIndex(key);
  if (rootIdx === -1) return [];
  const isMajor = tonality !== 'Minor';
  const flats = usesFlats(key, null, isMajor ? 'major' : 'minor');
  return (isMajor ? [0, 4, 7] : [0, 3, 7]).map((interval) => ({
    name: getNoteName(rootIdx + interval, flats),
    degree: DEGREE_LABELS[interval],
    isRoot: interval === 0,
  }));
}
